import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, auth } from '../api/client';
import { useToast } from '../components/Toast';
import type { Organization } from '../types/models';

const emptyOrg = {
  code: '', name: '', address: '', city: '', contactEmail: '', contactPhone: '', logoDataUrl: '',
};

/** Load an image file, shrink it so its longest side is <= maxDim, and return a PNG data URL.
 *  Keeps stored logos small (the whole thing lives inline in the org row). */
function fileToLogoDataUrl(file: File, maxDim = 512): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(reader.result as string); return; }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** A small square logo, or a coloured initials tile when the org has none. */
function LogoTile({ org, size = 40 }: { org: Organization; size?: number }) {
  const box: React.CSSProperties = {
    width: size, height: size, borderRadius: 8, objectFit: 'contain',
    background: 'var(--surface-2)', border: '1px solid var(--border)', flex: 'none',
  };
  if (org.logoDataUrl) return <img src={org.logoDataUrl} alt={`${org.name} logo`} style={box} />;
  return (
    <div style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, color: 'var(--muted)' }}>
      {(org.code || org.name || '?').slice(0, 2).toUpperCase()}
    </div>
  );
}

export function OrganizationsPage() {
  const nav = useNavigate();
  const user = auth.getUser();
  const toast = useToast();

  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyOrg });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Platform admin only.
  useEffect(() => {
    if (user?.role !== 'Admin') { nav('/exams', { replace: true }); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    try {
      setOrgs(await api.listOrganizations());
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load organizations.');
    }
  }

  function openCreate() {
    setEditId(null);
    setForm({ ...emptyOrg });
    setFormError(null);
    setShowModal(true);
  }

  function openEdit(o: Organization) {
    setEditId(o.id);
    setForm({
      code: o.code, name: o.name,
      address: o.address ?? '', city: o.city ?? '',
      contactEmail: o.contactEmail ?? '', contactPhone: o.contactPhone ?? '',
      logoDataUrl: o.logoDataUrl ?? '',
    });
    setFormError(null);
    setShowModal(true);
  }

  async function onLogoFile(file: File | null) {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setFormError('Please choose an image file (PNG, JPG…).'); return; }
    if (file.size > 5 * 1024 * 1024) { setFormError('That image is too large — pick one under 5 MB.'); return; }
    try {
      const url = await fileToLogoDataUrl(file);
      setForm(f => ({ ...f, logoDataUrl: url }));
      setFormError(null);
    } catch {
      setFormError('Could not read that image.');
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      contactEmail: form.contactEmail.trim() || null,
      contactPhone: form.contactPhone.trim() || null,
      logoDataUrl: form.logoDataUrl || null,
    };
    try {
      if (editId) {
        await api.updateOrganization(editId, payload);
        toast.success('Organization updated.');
      } else {
        await api.createOrganization({ code: form.code.trim(), ...payload });
        toast.success('Organization created.');
      }
      setShowModal(false);
      await refresh();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to save organization.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  function logout() { auth.clear(); nav('/login'); }

  return (
    <>
      <header className="topbar">
        <div className="brand">Exam App · Platform</div>
        <div className="row" style={{ gap: 12 }}>
          <span style={{ fontSize: 13 }}>{user?.fullName} · Platform Admin</span>
          <button onClick={logout} className="topbar-ghost">Sign out</button>
        </div>
      </header>

      <div className="container">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: '0 0 4px' }}>Organizations</h1>
            <div className="muted">Create an organization, then sign in as its admin to manage academics.</div>
          </div>
          <div className="row" style={{ gap: 12 }}>
            <span className="muted">{orgs?.length ?? 0} total</span>
            <button className="primary" onClick={openCreate}>+ Add organization</button>
          </div>
        </div>

        {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

        <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>S.no</th>
                <th style={{ width: 64 }}>Logo</th>
                <th>Name</th>
                <th>Code</th>
                <th>Created</th>
                <th style={{ width: 220 }}></th>
              </tr>
            </thead>
            <tbody>
              {!orgs && <tr><td colSpan={6} className="muted">Loading…</td></tr>}
              {orgs && orgs.length === 0 && (
                <tr><td colSpan={6} className="muted">No organizations yet. Click “Add organization”.</td></tr>
              )}
              {orgs?.map((o, i) => (
                <tr key={o.id}>
                  <td>{i + 1}</td>
                  <td><LogoTile org={o} /></td>
                  <td style={{ fontWeight: 600 }}>{o.name}</td>
                  <td className="slug">{o.code}</td>
                  <td>{new Date(o.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button className="link-btn" onClick={() => openEdit(o)}>Edit</button>
                    <button className="link-btn" onClick={() => nav(`/org/${o.id}/institutions`)}>
                      Sign in as Admin →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-backdrop" onMouseDown={() => setShowModal(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>{editId ? 'Edit organization' : 'Create new organization'}</h2>
                <div className="muted">Top-level tenant. Institutions and departments live inside it.</div>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)} aria-label="Close">✕</button>
            </div>
            <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
              {/* Logo uploader */}
              <div className="row" style={{ gap: 16, alignItems: 'center' }}>
                {form.logoDataUrl
                  ? <img src={form.logoDataUrl} alt="Logo preview"
                         style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'contain',
                                  border: '1px solid var(--border)', background: 'var(--surface-2)' }} />
                  : <div style={{ width: 64, height: 64, borderRadius: 10, border: '1px dashed var(--border)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  color: 'var(--muted)', fontSize: 12 }}>No logo</div>}
                <div className="col" style={{ gap: 6 }}>
                  <div className="muted">Logo</div>
                  <div className="row" style={{ gap: 8 }}>
                    <button type="button" onClick={() => fileRef.current?.click()}>
                      {form.logoDataUrl ? 'Change logo' : 'Upload logo'}
                    </button>
                    {form.logoDataUrl && (
                      <button type="button" className="link-btn"
                              onClick={() => setForm(f => ({ ...f, logoDataUrl: '' }))}>Remove</button>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>PNG or JPG, up to 5 MB. Resized automatically.</div>
                  <input ref={fileRef} type="file" accept="image/*" hidden
                         onChange={e => { onLogoFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                </div>
              </div>

              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: '0 0 160px' }}>
                  <div className="muted">Code *</div>
                  <input value={form.code} required maxLength={32} disabled={!!editId}
                         onChange={e => setForm({ ...form, code: e.target.value })} placeholder="DET" />
                  {editId && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Code can't be changed.</div>}
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Name *</div>
                  <input value={form.name} required maxLength={300}
                         onChange={e => setForm({ ...form, name: e.target.value })}
                         placeholder="Demo Education Trust" />
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Address</div>
                  <input value={form.address} maxLength={500}
                         onChange={e => setForm({ ...form, address: e.target.value })} />
                </label>
                <label style={{ flex: '0 0 180px' }}>
                  <div className="muted">City</div>
                  <input value={form.city} maxLength={120}
                         onChange={e => setForm({ ...form, city: e.target.value })} />
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Contact email</div>
                  <input type="email" value={form.contactEmail} maxLength={320}
                         onChange={e => setForm({ ...form, contactEmail: e.target.value })} />
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Contact phone</div>
                  <input value={form.contactPhone} maxLength={32}
                         onChange={e => setForm({ ...form, contactPhone: e.target.value })} />
                </label>
              </div>
              {formError && <div className="error">{formError}</div>}
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="primary" disabled={busy}>
                  {busy ? 'Saving…' : editId ? 'Save changes' : 'Create organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
